'use client';

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Brain } from 'lucide-react';

interface AssessmentResultsSectionProps {
  content: string;
}

/**
 * Specialized renderer for 量表评估结果 section
 * Parses and displays assessment results with visual enhancements
 */
export function AssessmentResultsSection({ content }: AssessmentResultsSectionProps) {
  // Parse the content to extract structured data
  const parseAssessmentContent = (text: string) => {
    const lines = text.split('\n');
    const result: {
      totalScore: string;
      maxScore: string;
      completionCount: string;
      completionTotal: string;
      completionPct: string;
      severity: string;
      severityLevel: 'normal' | 'mild' | 'moderate' | 'severe';
      questionAnswers: Array<{ index: number; questionText: string; answer: string }>;
    } = {
      totalScore: '0',
      maxScore: '0',
      completionCount: '0',
      completionTotal: '0',
      completionPct: '0',
      severity: '未知',
      severityLevel: 'normal',
      questionAnswers: []
    };

    let inQuestionSection = false;

    for (const line of lines) {
      // Parse total score line: "**量表总分**: X/Y分" (no percentage)
      const totalMatch = line.match(/\*\*量表总分\*\*:\s*(\d+)\/(\d+)分/);
      if (totalMatch) {
        result.totalScore = totalMatch[1];
        result.maxScore = totalMatch[2];
        continue;
      }

      // Parse completion line: "**完成度**: X/Y (Z%)" — completion rate = questions answered / total questions
      const completionMatch = line.match(/\*\*完成度\*\*:\s*(\d+)\/(\d+)\s*\(([\d.]+)%\)/);
      if (completionMatch) {
        result.completionCount = completionMatch[1];
        result.completionTotal = completionMatch[2];
        result.completionPct = completionMatch[3];
        continue;
      }

      // Parse severity line: "**严重程度**: XXX"
      const severityMatch = line.match(/\*\*严重程度\*\*:\s*(.+)/);
      if (severityMatch) {
        result.severity = severityMatch[1].trim();
        const severityLower = result.severity.toLowerCase();
        if (severityLower.includes('正常') || severityLower.includes('normal')) {
          result.severityLevel = 'normal';
        } else if (severityLower.includes('轻度') || severityLower.includes('mild')) {
          result.severityLevel = 'mild';
        } else if (severityLower.includes('中度') || severityLower.includes('moderate')) {
          result.severityLevel = 'moderate';
        } else if (severityLower.includes('重度') || severityLower.includes('severe')) {
          result.severityLevel = 'severe';
        }
        continue;
      }

      // Start of 题目与作答 section
      if (line.includes('### 题目与作答')) {
        inQuestionSection = true;
        continue;
      }

      // Parse question/answer lines: "- **N. 题目文本**：用户答案"
      if (inQuestionSection) {
        const qaMatch = line.match(/-\s*\*\*(\d+)\.\s*(.+?)\*\*[：:]\s*(.*)/);
        if (qaMatch) {
          result.questionAnswers.push({
            index: parseInt(qaMatch[1], 10),
            questionText: qaMatch[2].trim(),
            answer: (qaMatch[3] ?? '').trim()
          });
        }
      }
    }

    return result;
  };

  const data = parseAssessmentContent(content);

  // Severity badge colors
  const severityConfig = {
    normal: { label: '正常', color: 'bg-green-100 text-green-800 border-green-200' },
    mild: { label: '轻度', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    moderate: { label: '中度', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    severe: { label: '重度', color: 'bg-red-100 text-red-800 border-red-200' }
  };

  const config = severityConfig[data.severityLevel];

  return (
    <div className="space-y-6">
      {/* Total Score Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-blue-600 font-medium mb-1">量表总分</div>
            <div className="text-3xl font-bold text-blue-900">
              {data.totalScore}<span className="text-xl text-blue-600">/{data.maxScore}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-blue-600 font-medium mb-1">完成度</div>
            <div className="text-2xl font-bold text-blue-900">
              {data.completionCount}/{data.completionTotal} <span className="text-lg">({data.completionPct}%)</span>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <Badge className={`${config.color} text-sm px-3 py-1`}>
              {config.label}
            </Badge>
          </div>
        </div>
      </div>

      {/* 题目与作答: original scale questions + user choices */}
      {data.questionAnswers.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center">
            <Brain className="w-4 h-4 mr-2" />
            题目与作答
          </h4>
          <div className="space-y-3">
            {data.questionAnswers.map((qa, idx) => (
              <div key={idx} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="text-sm font-medium text-slate-800 mb-1">
                  {qa.index}. {qa.questionText}
                </div>
                <div className="text-slate-600 pl-4">
                  {qa.answer || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 评估建议 removed: detailed assessment is in the following Markdown report */}
    </div>
  );
}
